/**
 * Omgevingsconfiguratie, gelezen uit `window.ROOSTER_CONFIG` (public/config.js).
 *
 * Bewust géén Vite-`import.meta.env`: dan zou de API-URL bij het bouwen in de
 * bundel worden gebakken en was een andere omgeving een rebuild. Nu is het één
 * leesbaar bestandje naast de bundel, aan te passen door iemand van het IT-team
 * zonder Node of npm — hetzelfde uitgangspunt als vóór de overstap naar React.
 *
 * Alles hier heeft een werkende standaardwaarde, zodat een ontbrekende of half
 * ingevulde config.js een leeg scherm oplevert noch een crash.
 */

interface RuweConfig {
  apiBase?: unknown;
  pollIntervalSeconden?: unknown;
  backoffMaxSeconden?: unknown;
  verouderdNaSeconden?: unknown;
  autoUitlogSeconden?: unknown;
  foutMeldingSeconden?: unknown;
  scanHerhaalSeconden?: unknown;
  dagWisselTijd?: unknown;
}

declare global {
  interface Window {
    ROOSTER_CONFIG?: RuweConfig;
  }
}

export interface Config {
  /** Basis-URL van de API. Leeg = same-origin. */
  apiBase: string;
  pollIntervalMs: number;
  backoffMaxMs: number;
  verouderdNaSeconden: number;
  /** Na zoveel tijd sinds het inloggen gaat de tablet vanzelf terug naar inloggen. */
  autoUitlogMs: number;
  foutMeldingMs: number;
  /** Onderdruk alleen dezelfde pas binnen deze termijn. */
  scanHerhaalMs: number;
  dagWisselTijd: string;
}

const STANDAARD = {
  pollIntervalSeconden: 60,
  backoffMaxSeconden: 600,
  verouderdNaSeconden: 900,
  autoUitlogSeconden: 10,
} as const;

/** Leest een positief getal, of valt terug op de standaard. */
function getal(waarde: unknown, standaard: number): number {
  const n = typeof waarde === 'number' ? waarde : Number.parseFloat(String(waarde ?? ''));
  return Number.isFinite(n) && n > 0 ? n : standaard;
}

/**
 * Geëxporteerd los van `window` zodat het testbaar is zonder browser.
 */
export function leesConfig(ruw: RuweConfig | undefined): Config {
  // Afsluitende slashes eraf: anders wordt het '…workers.dev//api/rooster/…'.
  const apiBase = String(ruw?.apiBase ?? '').replace(/\/+$/, '');

  return {
    apiBase,
    scanHerhaalMs: getal(ruw?.scanHerhaalSeconden, 2) * 1000,
    foutMeldingMs: getal(ruw?.foutMeldingSeconden, 4) * 1000,
    dagWisselTijd: typeof ruw?.dagWisselTijd === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(ruw.dagWisselTijd) ? ruw.dagWisselTijd : '16:30',
    pollIntervalMs: getal(ruw?.pollIntervalSeconden, STANDAARD.pollIntervalSeconden) * 1000,
    backoffMaxMs: getal(ruw?.backoffMaxSeconden, STANDAARD.backoffMaxSeconden) * 1000,
    verouderdNaSeconden: getal(ruw?.verouderdNaSeconden, STANDAARD.verouderdNaSeconden),
    autoUitlogMs: getal(ruw?.autoUitlogSeconden, STANDAARD.autoUitlogSeconden) * 1000,
  };
}

export const config: Config = leesConfig(
  typeof window === 'undefined' ? undefined : window.ROOSTER_CONFIG,
);
